import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { acceptInvite, getInvite, InviteUnavailableError, type InvitePreview } from "@/lib/api";
import { evaluatePasswordRules, NO_CHARACTER_REQUIREMENTS } from "@/lib/password-policy";
import { AuthLogo } from "./AuthLogo";
import { PasswordRuleList } from "./PasswordRuleList";

type Status = "loading" | "ready" | "unavailable" | "submitting";

export function AcceptInvitePage() {
	const { inviteId } = useParams<{ inviteId: string }>();
	const navigate = useNavigate();

	const [status, setStatus] = useState<Status>("loading");
	const [invite, setInvite] = useState<InvitePreview | null>(null);
	const [unavailableReason, setUnavailableReason] = useState<"notFound" | "expired">("notFound");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const passwordRules = evaluatePasswordRules(
		password,
		invite ? [invite.email, invite.firstName, invite.lastName] : [],
		invite?.passwordRequirements ?? NO_CHARACTER_REQUIREMENTS,
	);

	useEffect(() => {
		if (!inviteId) {
			setUnavailableReason("notFound");
			setStatus("unavailable");
			return;
		}
		let cancelled = false;
		getInvite(inviteId)
			.then((data) => {
				if (cancelled) return;
				setInvite(data);
				setStatus("ready");
			})
			.catch((err) => {
				if (cancelled) return;
				if (err instanceof InviteUnavailableError) {
					setUnavailableReason(err.reason);
				}
				setStatus("unavailable");
			});
		return () => {
			cancelled = true;
		};
	}, [inviteId]);

	async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
		e.preventDefault();
		if (!inviteId) return;
		setError(null);

		if (passwordRules.some((rule) => !rule.satisfied)) {
			setError("パスワードの条件をすべて満たしてください。");
			return;
		}
		if (password !== confirmPassword) {
			setError("確認用パスワードが一致しません。");
			return;
		}

		setStatus("submitting");
		try {
			await acceptInvite(inviteId, password);
			navigate("/login", {
				replace: true,
				state: { message: "パスワードを設定しました。ログインしてください。" },
			});
		} catch (err) {
			if (err instanceof InviteUnavailableError) {
				setUnavailableReason(err.reason);
				setStatus("unavailable");
				return;
			}
			setError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
			setStatus("ready");
		}
	}

	return (
		<div
			style={{
				minHeight: "100vh",
				background: "var(--surface-2)",
				display: "grid",
				placeItems: "center",
				fontFamily: "var(--font-sans)",
			}}
		>
			<div
				style={{
					width: "100%",
					maxWidth: 400,
					background: "var(--surface-1)",
					borderRadius: "var(--radius-5)",
					boxShadow: "var(--shadow-card)",
					padding: 32,
					display: "flex",
					flexDirection: "column",
					gap: 24,
				}}
			>
				<header style={{ display: "flex", flexDirection: "column", gap: 4 }}>
					<AuthLogo />
					<h1
						style={{
							margin: 0,
							fontSize: "var(--text-display)",
							fontWeight: 600,
							lineHeight: "var(--leading-snug)",
							letterSpacing: "var(--tracking-display)",
							color: "var(--fg-1)",
						}}
					>
						パスワードを設定
					</h1>
					{status === "ready" && invite && (
						<p
							style={{
								margin: 0,
								fontSize: "var(--text-ui)",
								color: "var(--fg-2)",
								lineHeight: "var(--leading-ui)",
							}}
						>
							{invite.lastName} {invite.firstName}さん、{invite.organizationName}へようこそ。
						</p>
					)}
					{status === "loading" && (
						<p style={{ margin: 0, fontSize: "var(--text-ui)", color: "var(--fg-2)" }}>
							招待を確認しています…
						</p>
					)}
				</header>

				{status === "unavailable" && (
					<div>
						<p
							style={{
								margin: 0,
								fontSize: "var(--text-ui)",
								color: "var(--fg-2)",
								lineHeight: "var(--leading-body)",
							}}
						>
							{unavailableReason === "expired"
								? "この招待リンクは期限切れです。管理者に新しいリンクの発行を依頼してください。"
								: "この招待は無効か期限切れです。管理者に新しいリンクの発行を依頼してください。"}
						</p>
					</div>
				)}

				{(status === "ready" || status === "submitting") && invite && (
					<form
						onSubmit={handleSubmit}
						style={{ display: "flex", flexDirection: "column", gap: 12 }}
					>
						<label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<span
								style={{ fontSize: "var(--text-caption)", fontWeight: 500, color: "var(--fg-2)" }}
							>
								メールアドレス
							</span>
							<input
								type="email"
								value={invite.email}
								readOnly
								style={{
									height: 36,
									padding: "0 10px",
									fontSize: "var(--text-ui)",
									color: "var(--fg-2)",
									background: "var(--surface-2)",
									border: "none",
									borderRadius: "var(--radius-3)",
									boxShadow: "var(--shadow-ring)",
									outline: "none",
									fontFamily: "var(--font-sans)",
								}}
							/>
						</label>

						<label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<span
								style={{ fontSize: "var(--text-caption)", fontWeight: 500, color: "var(--fg-2)" }}
							>
								パスワード
							</span>
							<input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								autoComplete="new-password"
								style={inputStyle}
							/>
						</label>

						<PasswordRuleList rules={passwordRules} />

						<label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
							<span
								style={{ fontSize: "var(--text-caption)", fontWeight: 500, color: "var(--fg-2)" }}
							>
								パスワード（確認）
							</span>
							<input
								type="password"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								required
								autoComplete="new-password"
								style={inputStyle}
							/>
						</label>

						{error && (
							<div
								style={{
									padding: "8px 10px",
									borderRadius: "var(--radius-3)",
									background: "var(--danger-subtle)",
									boxShadow: "0 0 0 1px var(--danger-line)",
									fontSize: "var(--text-caption)",
									color: "var(--danger)",
									lineHeight: "var(--leading-ui)",
								}}
							>
								{error}
							</div>
						)}

						<button
							type="submit"
							disabled={status === "submitting"}
							style={{
								marginTop: 4,
								height: 36,
								padding: "0 16px",
								fontSize: "var(--text-ui)",
								fontWeight: 500,
								fontFamily: "var(--font-sans)",
								color:
									status === "submitting" ? "var(--on-neutral-solid)" : "var(--on-accent-solid)",
								background:
									status === "submitting" ? "var(--neutral-solid)" : "var(--accent-solid)",
								border: "none",
								borderRadius: "var(--radius-3)",
								cursor: status === "submitting" ? "not-allowed" : "pointer",
								letterSpacing: "var(--tracking-tight)",
							}}
						>
							{status === "submitting" ? "設定中…" : "パスワードを設定"}
						</button>
					</form>
				)}
			</div>
		</div>
	);
}

const inputStyle: React.CSSProperties = {
	height: 36,
	padding: "0 10px",
	fontSize: "var(--text-ui)",
	color: "var(--fg-1)",
	background: "var(--surface-1)",
	border: "none",
	borderRadius: "var(--radius-3)",
	boxShadow: "var(--shadow-ring)",
	outline: "none",
	fontFamily: "var(--font-sans)",
};
